import Vue from 'vue';
import moment from 'moment';
import VueApexCharts from 'vue-apexcharts';
import VModal from 'vue-js-modal';
import VueClipboard from 'vue-clipboard2';
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';

import { LMap, LTileLayer, LMarker } from 'vue2-leaflet';
import { Icon, latLng } from 'leaflet';
import 'leaflet/dist/leaflet.css';

import App from './App.vue';
// import './registerServiceWorker';
import router from './router';
import store from './store';
import api from './api';
import dateUtils from './_libs/dateUtils';

import HScroll from '@/components/HScroll.vue';
import GenericCard from '@/components/GenericCard.vue';
import MenuItem from '@/components/MenuItem.vue';
import ActionSelectorMenu from '@/components/ActionSelectorMenu.vue';
import CustomAreaFilter from './components/CustomAreaFilter.vue';
import CustomCategoriesFilter from './components/CustomCategoriesFilter.vue';
import CustomCheckBox from './components/CustomCheckBox.vue';
import CustomDatePicker from './components/CustomDatePicker.vue';
import CustomDatePickerSingle from './components/CustomDatePickerSingle.vue';
import CustomFileUpload from './components/CustomFileUpload.vue';
import CustomInputSelect from './components/CustomInputSelect.vue';
import CustomInputSelectJson from './components/CustomInputSelectJson.vue';
import CustomPagination from '@/components/CustomPagination.vue';
import CustomPaginationSuperCompact from '@/components/CustomPaginationSuperCompact.vue';
import CustomSearchBox from '@/components/CustomSearchBox.vue';
import CustomTable from '@/components/CustomTable.vue';
import EditableAvatar from '@/components/EditableAvatar.vue';
import FormattedEntryNo from '@/components/FormattedEntryNo.vue';
import PanelTitleRight from '@/components/PanelTitleRight.vue';
import LoadedContentWrapper from '@/components/LoadedContentWrapper.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import LoadingError from '@/components/LoadingError.vue';
import StatusDialog from '@/components/_dialogs/StatusDialog.vue';
import InfoTooltip from '@/components/InfoTooltip.vue';

import InputWithIconSlot from '@/components/InputWithIconSlot.vue';
import DataItem from '@/components/DataItem.vue';

import StatItem from '@/components/dashboard/StatItem.vue';
import UserPicture from '@/components/UserPicture.vue';
import NumberWithDescription from '@/components/NumberWithDescription.vue';

import CustomTab from '@/components/CustomTab.vue';
import CustomStatusBadge from '@/components/CustomStatusBadge.vue';

window.BUILD_NOTE = process.env.VUE_APP_BUILD_NOTE;
console.log('Build:', window.BUILD_NOTE);

Vue.config.productionTip = false;

Vue.prototype.$api = api;

Vue.use(VModal, { dialog: true });
Vue.use(VueApexCharts);

Vue.component('LMap', LMap);
Vue.component('LTileLayer', LTileLayer);
Vue.component('LMarker', LMarker);

Vue.prototype.$latLng = latLng;

delete Icon.Default.prototype._getIconUrl; // eslint-disable-line

Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'), // eslint-disable-line
  iconUrl: require('leaflet/dist/images/marker-icon.png'), // eslint-disable-line
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'), // eslint-disable-line
});

Vue.prototype.$dictionary = {
  receiptType: {
    0: 'บิลมี VAT',
    1: 'บิลไม่มี VAT',
  },
  deliveryType: {
    store: 'ส่งหน้าร้าน',
    branch_and_site: 'ส่งสาขา/ส่งโครงการ',
    pickup: 'รับสินค้าเอง',
    forward: 'ส่งต่อ',
  },
};

Vue.prototype.$tableColumns = {
  saleProducts: {
    productCode: {
      title: 'รหัสสินค้า',
      align: 'left',
      computed(row) {
        return row.product.productCode;
      },
    },
    productName: {
      title: 'ชื่อสินค้า',
      align: 'left',
      computed(row) {
        return row.product.productName;
      },
    },
    amount: {
      title: 'จำนวน',
      align: 'right',
      type: api.product.COL_TYPE.Amount,
    },
    weight: {
      title: 'น้ำหนักรวม',
      align: 'right',
      type: Number,
      computed(row) {
        return parseFloat(row.product.weight) * parseFloat(row.amount);
      },
    },
    sellPrice: {
      title: 'ราคาขายรวมคอม',
      align: 'right',
      type: Number,
    },
    total: {
      title: 'ยอดรวม',
      align: 'right',
      type: Number,
      computed(row) {
        return Number(row.amount) * Number(row.sellPrice);
      },
    },
  },
};

Vue.component('HScroll', HScroll);
Vue.component('GenericCard', GenericCard);
Vue.component('MenuItem', MenuItem);
Vue.component('ActionSelectorMenu', ActionSelectorMenu);
Vue.component('CustomAreaFilter', CustomAreaFilter);
Vue.component('CustomCategoriesFilter', CustomCategoriesFilter);
Vue.component('CustomCheckBox', CustomCheckBox);
Vue.component('CustomDatePicker', CustomDatePicker);
Vue.component('CustomDatePickerSingle', CustomDatePickerSingle);
Vue.component('CustomFileUpload', CustomFileUpload);
Vue.component('CustomInputSelect', CustomInputSelect);
Vue.component('CustomInputSelectJson', CustomInputSelectJson);
Vue.component('CustomPagination', CustomPagination);
Vue.component('CustomPaginationSuperCompact', CustomPaginationSuperCompact);
Vue.component('CustomSearchBox', CustomSearchBox);
Vue.component('CustomTable', CustomTable);
Vue.component('EditableAvatar', EditableAvatar);
Vue.component('FormattedEntryNo', FormattedEntryNo);
Vue.component('PanelTitleRight', PanelTitleRight);
Vue.component('LoadedContentWrapper', LoadedContentWrapper);
Vue.component('LoadingIndicator', LoadingIndicator);
Vue.component('LoadingError', LoadingError);
Vue.component('StatusDialog', StatusDialog);

Vue.component('InfoTooltip', InfoTooltip);

Vue.component('InputWithIconSlot', InputWithIconSlot);
Vue.component('DataItem', DataItem);

// dashboard
Vue.component('StatItem', StatItem);
Vue.component('UserPicture', UserPicture);
Vue.component('NumberWithDescription', NumberWithDescription);

Vue.filter('currency', (val) => {
  const res = parseFloat(val).toFixed(2).replace(/\B(?=(?=\d*\.)(\d{3})+(?!\d))/g, ',');
  if (Number.isNaN(res)) return '-';
  return res;
});

// dialog
Vue.component('CustomTab', CustomTab);
Vue.component('CustomStatusBadge', CustomStatusBadge);

Vue.component('apexchart', VueApexCharts);

// clipboard
VueClipboard.config.autoSetContainer = true;
Vue.use(VueClipboard);

moment.locale('th');
Vue.prototype.$moment = moment;
Vue.prototype.$dateUtils = dateUtils;

Vue.directive('unix', {
  inserted(el) { /* eslint-disable */
    console.log(el);
    el.style.cssText = 'position: relative';
    const block = document.createElement('div');
    block.style.cssText = 'pointer-events: none; z-index: 11; text-align: center; position: absolute; top: 0; left: 0; bottom: 0; right: 0; background: rgba(255, 0, 0, 0.3); color: black; font-size: 10px; display: flex; justify-content: center; align-items: center;';
    block.innerHTML = 'MOCKED DATA / WAIT UNIX';
    el.prepend(block);
  },
});

Sentry.init({
  dsn: 'https://8813e265c76046bea8b87468b4806430@sentry.io/5188415',
  integrations: [new Integrations.Vue({ Vue, attachProps: true, logErrors: true })],
});

navigator.serviceWorker.getRegistrations().then(
  function (registrations) {
    console.log('de-registering service worker');
    for (let registration of registrations) {
      registration.unregister();
    }
  }
);

new Vue({
  router,
  store,
  render: h => h(App),
}).$mount('#app');
